import { KunSseActivity } from "../../components/genie/kunState";
import {
  getConversationStatusUpdateFromSSE,
  getOutlookTagUpdateFromSSE,
} from "./emailSseRefresh";
import { describeNewEmail, getNewEmailSignal, lookupNewEmailMessage } from "./newEmailActivity";

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;

export function getSseEventName(payload: unknown): string {
  const envelope = asRecord(payload);
  const parameters = asRecord(envelope?.parameters) ?? envelope;
  const events = parameters && Array.isArray(parameters.events) ? parameters.events : [];
  const latestEvent = [...events].reverse().map(asRecord).find(Boolean);
  return String(
    parameters?.event ||
      latestEvent?.event_type ||
      latestEvent?.event ||
      envelope?.type ||
      (typeof payload === "string" ? payload : "") ||
      ""
  )
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_");
}

export const isSseHeartbeat = (payload: unknown): boolean => {
  const name = getSseEventName(payload);
  return name === "HEARTBEAT" || name === "KEEPALIVE";
};

// Backend-to-backend signals (email-task, agent) rather than user-facing
// export/import notifications. These are what the mascot speaks.
export const isSseSignal = (payload: unknown): boolean => asRecord(payload)?.type === "SIGNAL";

export function isNewEmailSsePayload(payload: unknown): boolean {
  const eventName = getSseEventName(payload);
  const title = (asRecord(payload)?.title as string | undefined)?.trim().toUpperCase() ?? "";
  return (
    eventName.includes("NEW_EMAIL") ||
    eventName.includes("NEW_MAIL") ||
    eventName.includes("EMAIL_RECEIVED") ||
    eventName.includes("MAIL_RECEIVED") ||
    eventName.includes("NEW_MESSAGE") ||
    eventName.includes("MESSAGE_RECEIVED") ||
    title.includes("NEW EMAIL") ||
    title.includes("NEW MESSAGE")
  );
}

// Turns one SSE payload into what the mascot should say, or null when there is
// nothing to say (heartbeats). New emails are looked up for sender/subject.
export async function activityFromSsePayload(payload: unknown): Promise<KunSseActivity | null> {
  if (isSseHeartbeat(payload)) return null;
  const envelope = asRecord(payload);
  const parameters = asRecord(envelope?.parameters) ?? envelope;
  const eventName = getSseEventName(payload);
  const title = typeof envelope?.title === "string" ? envelope.title.trim() : "";
  const statusUpdate = getConversationStatusUpdateFromSSE(payload);
  const outlookTagUpdate = getOutlookTagUpdateFromSSE(payload);
  const identity = String(
    parameters?.message_id ||
      parameters?.messageId ||
      parameters?.conversation_id ||
      parameters?.conversationId ||
      envelope?.id ||
      Date.now()
  );
  const id = `sse:${eventName || "activity"}:${identity}:${
    statusUpdate?.taskStatus || outlookTagUpdate?.eventType || ""
  }`;

  if (isNewEmailSsePayload(payload)) {
    const signal = getNewEmailSignal(payload);
    const message = await lookupNewEmailMessage(signal);
    return { id, kind: "email", ...describeNewEmail(signal, message) };
  }

  let message = "New email activity received";
  if (statusUpdate) {
    message = `Conversation status changed to ${statusUpdate.taskStatus
      .toLowerCase()
      .replace(/_/g, " ")}`;
  } else if (outlookTagUpdate) {
    message =
      outlookTagUpdate.eventType === "OUTLOOK_TAG_CLEARED"
        ? "Email category was cleared"
        : `Email category updated${outlookTagUpdate.status ? ` to ${outlookTagUpdate.status}` : ""}`;
  } else if (title) {
    message = title;
  } else if (eventName) {
    const label = eventName.toLowerCase().replace(/_/g, " ");
    message = label.charAt(0).toUpperCase() + label.slice(1);
  }
  return { id, kind: isSseSignal(payload) ? "status" : "notification", message };
}
